import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position (only on home page)
      if (location.pathname === '/') {
        const sections = ['home', 'tours', 'destinations', 'about', 'contact'];
        const scrollPosition = window.scrollY + 100; // Offset for navbar
        
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Handle smooth scroll for anchor links
  const handleAnchorClick = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    // If we're not on the home page, go to home page first with hash
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    // Scroll to section on home page
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Navbar height
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  // Check if a link is active
  const isActive = (section) => {
    if (location.pathname !== '/') return false;
    return activeSection === section;
  };

  // WhatsApp message template
  const whatsappMessage = "Hello! I'm interested in booking a tour with Encourage Women Ethiopia. Can you help me plan my trip?";
  const whatsappUrl = `https://wa.me/251912093264?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-white shadow-md py-2' 
        : 'bg-transparent py-4'
    }`}>
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between">
          
          {/* ===== LOGO - EXTRA LARGE AS REQUESTED ===== */}
          <Link to="/" className="flex items-center">
            <img 
              src="/images/encourage-women-logo.png" 
              alt="Encourage Women Ethiopia"
              className="h-24 md:h-32 w-auto transition-all duration-300"
            />
          </Link>

          {/* ===== DESKTOP MENU - ANCHOR LINKS WITH ACTIVE STATE ===== */}
          <div className="hidden md:flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center gap-8">
              {/* Home Link */}
              <a 
                href="/#home"
                onClick={(e) => handleAnchorClick(e, 'home')}
                className={`font-medium text-sm transition-colors duration-200 ${
                  scrolled 
                    ? isActive('home') 
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-gray-600 hover:text-[#FFD700]' 
                    : isActive('home') && location.pathname === '/'
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-white/90 hover:text-[#FFD700]'
                }`}
              >
                Home
              </a>
              
              {/* Tours Link */}
              <a 
                href="/#tours"
                onClick={(e) => handleAnchorClick(e, 'tours')}
                className={`font-medium text-sm transition-colors duration-200 ${
                  scrolled 
                    ? isActive('tours') 
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-gray-600 hover:text-[#FFD700]' 
                    : isActive('tours') && location.pathname === '/'
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-white/90 hover:text-[#FFD700]'
                }`}
              >
                Tours
              </a>
              
              {/* Destinations Link */}
              <a 
                href="/#destinations"
                onClick={(e) => handleAnchorClick(e, 'destinations')}
                className={`font-medium text-sm transition-colors duration-200 ${
                  scrolled 
                    ? isActive('destinations') 
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-gray-600 hover:text-[#FFD700]' 
                    : isActive('destinations') && location.pathname === '/'
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-white/90 hover:text-[#FFD700]'
                }`}
              >
                Destinations
              </a>
              
              {/* About Us Link */}
              <a 
                href="/#about"
                onClick={(e) => handleAnchorClick(e, 'about')}
                className={`font-medium text-sm transition-colors duration-200 ${
                  scrolled 
                    ? isActive('about') 
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-gray-600 hover:text-[#FFD700]' 
                    : isActive('about') && location.pathname === '/'
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-white/90 hover:text-[#FFD700]'
                }`}
              >
                About Us
              </a>
              
              {/* Contact Link */}
              <a 
                href="/#contact"
                onClick={(e) => handleAnchorClick(e, 'contact')}
                className={`font-medium text-sm transition-colors duration-200 ${
                  scrolled 
                    ? isActive('contact') 
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-gray-600 hover:text-[#FFD700]' 
                    : isActive('contact') && location.pathname === '/'
                      ? 'text-[#FFD700] font-semibold' 
                      : 'text-white/90 hover:text-[#FFD700]'
                }`}
              >
                Contact
              </a>
            </div>
          </div>
          
          {/* ===== BOOK NOW BUTTON - GOLD with "Book Now" text ===== */}
          <div className="hidden md:block">
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 inline-flex items-center gap-2 ${
                scrolled 
                  ? 'bg-[#FFD700] text-gray-900 hover:bg-[#FFC800] shadow-md' 
                  : 'bg-[#FFD700] text-gray-900 hover:bg-[#FFC800] shadow-md'
              }`}
            >
              Book Now
            </a>
          </div>

          {/* ===== MOBILE MENU BUTTON ===== */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-2xl focus:outline-none"
            aria-label="Toggle menu"
          >
            <span className={scrolled ? 'text-gray-900' : 'text-white'}>
              {mobileMenuOpen ? '✕' : '☰'}
            </span>
          </button>
        </div>

        {/* ===== MOBILE MENU DROPDOWN - ANCHOR LINKS ===== */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-white/20 pt-4">
            <div className="flex flex-col gap-3">
              {/* Mobile Home Link */}
              <a 
                href="/#home"
                onClick={(e) => handleAnchorClick(e, 'home')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Home
              </a>
              
              {/* Mobile Tours Link */}
              <a 
                href="/#tours"
                onClick={(e) => handleAnchorClick(e, 'tours')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Tours
              </a>
              
              {/* Mobile Destinations Link */}
              <a 
                href="/#destinations"
                onClick={(e) => handleAnchorClick(e, 'destinations')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Destinations
              </a>
              
              {/* Mobile About Us Link */}
              <a 
                href="/#about"
                onClick={(e) => handleAnchorClick(e, 'about')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                About Us
              </a>
              
              {/* Mobile Contact Link */}
              <a 
                href="/#contact"
                onClick={(e) => handleAnchorClick(e, 'contact')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Contact
              </a>
              
              {/* Mobile Book Now Button - GOLD with "Book Now" text */}
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`px-5 py-3 rounded-full font-semibold text-sm text-center transition-all ${
                  scrolled 
                    ? 'bg-[#FFD700] text-gray-900 hover:bg-[#FFC800]' 
                    : 'bg-[#FFD700] text-gray-900 hover:bg-[#FFC800]'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Book Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;